import { lazy, useEffect, useMemo, useState } from "react";
import Suspense from "common/Suspense";
import { Navigate, matchPath, useLocation, useRoutes } from "react-router-dom";
import { configureRoutes } from "utils/RouteUtils";
import { RouteEnum } from "constants/RouteConstants";
import { Container } from "@mui/material";
import PublicHeader from "./common/PublicHeader";
import PublicFooter from "./common/PublicFooter";
import Cookies from "./pages/cookies/Cookies";
import { useSearchParams } from "react-router-dom";
import { urlSearchParamsExtractor } from "utils/URLUtils";

function AppDefault() {
  const location = useLocation();

  const [searchParams] = useSearchParams();

  const { viewMode } = urlSearchParamsExtractor(searchParams, {
    viewMode: "mobile",
  });

  const [showCookies, setShowCookies] = useState(false);

  useEffect(() => {
    const cookiesTimeout = setTimeout(() => {
      setShowCookies(true);
    }, 5000);

    const storedCookies = localStorage.getItem("goCaby_cookies");
    if (storedCookies === "true") {
      clearTimeout(cookiesTimeout);
    }
  }, []);

  const onAddCookies = () => {
    localStorage.setItem("goCaby_cookies", "true");
    setShowCookies(false);
  };

  const routeConfig = useMemo(
    () =>
      [
        { path: RouteEnum.HOME, header: true, footer: true },
        { path: RouteEnum.PARTNER, header: true, footer: true },
        { path: RouteEnum.ABOUT_US, header: true, footer: true },
        { path: RouteEnum.CAREER, header: true, footer: true },
        { path: RouteEnum.CONTACT, header: true, footer: true },
        { path: RouteEnum.ABOUT_HOW_GO_WORKS, header: true, footer: true },
        { path: RouteEnum.GET_ESTIMATE, header: true, footer: true },
        { path: RouteEnum.GET_ESTIMATE_VALUE, header: true, footer: true },
        {
          path: RouteEnum.LEARN,
          content: true,
          header: viewMode === "desktop",
          footer: false,
        },
      ].find((route) => matchPath(route, location.pathname)),
    [location.pathname, viewMode]
  );

  const routes = useRoutes(ROUTES);

  return (
    <>
      {(routeConfig ? !!routeConfig.header : true) && (
        <PublicHeader elevation={0} />
      )}
      {(routeConfig ? !!routeConfig.content : true) ? (
        <Container className="min-h-full p-4 md:p-8">
          <Suspense>{routes}</Suspense>
        </Container>
      ) : (
        <Suspense>{routes}</Suspense>
      )}
      {(routeConfig ? !!routeConfig.footer : true) && <PublicFooter />}
      {showCookies && (
        <Cookies setShowCookies={setShowCookies} onAddCookies={onAddCookies} />
      )}
    </>
  );
}

const ROUTES = configureRoutes([
  {
    path: "*",
    element: <Navigate to={RouteEnum.HOME} replace />,
  },
  {
    path: RouteEnum.HOME,
    element: lazy(() => import("apps/default/pages/home/Home")),
  },
  {
    path: RouteEnum.PARTNER,
    element: lazy(() => import("apps/default/pages/partner/Partner")),
  },
  {
    path: RouteEnum.CAREER,
    element: lazy(() => import("apps/default/pages/career/Career")),
  },
  {
    path: RouteEnum.ABOUT_US,
    element: lazy(() => import("apps/default/pages/about/About")),
  },
  {
    path: RouteEnum.ABOUT_HOW_GO_WORKS,
    element: lazy(() => import("apps/default/pages/about/AboutHowGoWorks")),
  },
  {
    path: RouteEnum.CONTACT,
    element: lazy(() => import("apps/default/pages/contact/Contact")),
  },
  {
    path: RouteEnum.PRIVACY_AND_POLICY,
    element: lazy(() =>
      import("apps/default/pages/privacy-and-policy/PrivacyAndPolicy")
    ),
  },
  {
    path: RouteEnum.PRIVACY_POLICY,
    element: lazy(() =>
      import("apps/default/pages/privacy-and-policy/PrivacyAndPolicy")
    ),
  },
  {
    path: RouteEnum.TERMS_CONDITIONS,
    element: lazy(() =>
      import("apps/default/pages/terms-and-conditions/TermsAndConditions")
    ),
  },
  {
    path: RouteEnum.TERMS_AND_CONDITIONS,
    element: lazy(() =>
      import("apps/default/pages/terms-and-conditions/TermsAndConditions")
    ),
  },
  {
    path: RouteEnum.DOWNLOAD_APP,
    element: lazy(() => import("apps/default/pages/DownloadApp")),
  },

  {
    path: RouteEnum.DOWNLOAD_APP_SHORT,
    element: lazy(() => import("apps/default/pages/DownloadApp")),
  },
  {
    path: RouteEnum.GET_ESTIMATE,
    element: lazy(() => import("apps/default/pages/getEstimate/GetEstimate")),
  },
  {
    path: RouteEnum.GET_ESTIMATE_VALUE,
    element: lazy(() =>
      import("apps/default/pages/getEstimate/GetEstimatedValue")
    ),
  },
  {
    path: RouteEnum.LEARN,
    element: lazy(() => import("apps/default/pages/learn/Learn")),
  },
]);

export default AppDefault;
